/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Put THIS recipe on one or more days.
 *
 * The other direction from the Plan screen's add button: there you start from a
 * day and pick a recipe, here you start from a recipe and pick days. Both exist
 * because both are how people actually think about it, and neither should be a
 * detour through the other.
 *
 * ── THE DATE ────────────────────────────────────────────────────────────────
 * The fortnight offered starts at the SERVER's today, from useToday — never
 * `new Date()`. A device clock an hour behind midnight would otherwise offer
 * yesterday as the first option.
 */

import { useState } from "react";

import { PlanApi } from "../../api/endpoints.ts";
import type { Recipe } from "../../api/types.ts";
import { addDays, relativeDayLabel } from "../../lib/dates.ts";
import { useToday } from "../../lib/useToday.tsx";
import { Modal } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";

export function AddToPlanModal({ recipe, slots, onClose, onAdded }: {
  recipe: Recipe;
  slots: Array<{ key: string; label: string }>;
  onClose: () => void;
  onAdded?: () => void;
}) {
  const { today } = useToday();
  const { lang } = usePrefs();
  const toast = useToast();

  const [slotKey, setSlotKey] = useState(
    slots.some((s) => s.key === "dinner") ? "dinner" : (slots[0]?.key ?? "dinner"),
  );
  const [servings, setServings] = useState(String(recipe.servings_base));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const days = today ? Array.from({ length: 14 }, (_, i) => addDays(today, i)) : [];

  const toggle = (d: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });

  const save = async () => {
    setSaving(true);
    let failed = 0;
    for (const date of [...selected].sort()) {
      const body: Record<string, unknown> = {
        planDate: date, slotKey, recipeId: recipe.id,
      };
      const n = Number(servings.replace(",", "."));
      if (n > 0) body.servings = n;
      try {
        await PlanApi.add(body);
      } catch {
        failed += 1;
      }
    }
    setSaving(false);
    if (failed > 0) {
      toast(`${failed} of ${selected.size} couldn't be added.`, "danger");
      return;
    }
    toast(`Added to ${selected.size} day${selected.size === 1 ? "" : "s"}`, "ok");
    onAdded?.();
    onClose();
  };

  return (
    <Modal title={`Plan "${recipe.title}"`} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm">Meal</span>
            <select className="input mt-1" value={slotKey}
                    onChange={(e) => setSlotKey(e.target.value)}>
              {slots.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm">Servings</span>
            <input className="input mt-1" inputMode="decimal" value={servings}
                   onChange={(e) => setServings(e.target.value)} />
          </label>
        </div>

        <div>
          <div className="label-mono mb-2">Which days</div>
          <div className="grid max-h-64 grid-cols-2 gap-1 overflow-y-auto sm:grid-cols-3">
            {days.map((d) => (
              <button key={d} type="button" onClick={() => toggle(d)}
                      className={`rounded-lg border px-2 py-1.5 text-left text-sm transition ${
                        selected.has(d)
                          ? "border-accent bg-accent/10 text-accent"
                          : "border-rule-soft hover:border-accent/40"}`}>
                {selected.has(d) && <span aria-hidden>✓ </span>}
                {today ? relativeDayLabel(d, today, lang) : d}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-rule-soft pt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={saving || selected.size === 0}
                  onClick={() => void save()}>
            {saving ? "Adding…" : `Add to ${selected.size || ""} ${selected.size === 1 ? "day" : "days"}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
